<template>
    <div class="filter-bar">
        <!-- Device Type Filter -->
        <div class="filter-group">
            <label>Type:</label>
            <select v-model="filters.type" @change="emitFilters">
                <option value="all">All Devices</option>
                <option value="smartphone">Smartphones</option>
                <option value="tablet">Tablets</option>
            </select>
        </div>

        <!-- OS Filter -->
        <div class="filter-group">
            <label>OS:</label>
            <select v-model="filters.os" @change="emitFilters">
                <option value="all">All OS</option>
                <option value="iOS">iOS</option>
                <option value="Android">Android</option>
                <option value="iPadOS">iPadOS</option>
                <option value="Windows">Windows</option>
            </select>
        </div>

        <!-- RAM Filter -->
        <div class="filter-group">
            <label>RAM:</label>
            <select v-model="filters.ram" @change="emitFilters">
                <option value="all">All RAM</option>
                <option value="<2GB">&lt; 2GB</option>
                <option value="2-4GB">2GB - 4GB</option>
                <option value="4-8GB">4GB - 8GB</option>
                <option value=">8GB">&gt; 8GB</option>
            </select>
        </div>

        <!-- Team Filter -->
        <div class="filter-group">
            <label>Team:</label>
            <select v-model="filters.team" @change="emitFilters">
                <option value="all">All Teams</option>
                <option value="Top Eleven">Top Eleven</option>
                <option value="Golf Rival">Golf Rival</option>
                <option value="Top Goal">Top Goal</option>
            </select>
        </div>

        <!-- Owner Filter -->
        <div class="filter-group">
            <label>Owner:</label>
            <select v-model="filters.owner" @change="emitFilters">
                <option value="all">All Owners</option>
                <option v-for="owner in owners" :key="owner" :value="owner">{{ owner }}</option>
            </select>
        </div>

        <!-- Status Filter -->
        <div class="filter-group">
            <label>Status:</label>
            <select v-model="filters.status" @change="emitFilters">
                <option value="all">All Status</option>
                <option value="available">Available</option>
                <option value="taken">Taken</option>
            </select>
        </div>

        <!-- Clear Filters -->
        <button @click="clearFilters" class="clear-btn">Clear All</button>
    </div>
</template>

<script setup>
import {ref, watch, onMounted} from "vue";
import {collection, getDocs} from "firebase/firestore";
import {db} from "@/firebase";
import {COLORS} from "@/styles/brand";

const props = defineProps({
    selectedFilter: {
        type: [String, Object],
        required: true,
    },
});

const emit = defineEmits(["filter-change"]);

const filters = ref({
    type: "all",
    os: "all",
    ram: "all",
    team: "all",
    owner: "all",
    status: "all",
});

const owners = ref([]);

const loadOwners = async () => {
    const querySnapshot = await getDocs(collection(db, "devices"));
    const ownerSet = new Set();
    querySnapshot.docs.forEach(doc => {
        const device = doc.data();
        if (device.owner && device.owner.trim()) {
            ownerSet.add(device.owner);
        }
    });
    owners.value = Array.from(ownerSet).sort();
};

const emitFilters = () => {
    emit("filter-change", filters.value);
};

const clearFilters = () => {
    filters.value = {type: "all", os: "all", ram: "all", team: "all", owner: "all", status: "all"};
    emitFilters();
};

// Initialize filters from props
const initializeFilters = () => {
    if (typeof props.selectedFilter === "object") {
        filters.value = {...props.selectedFilter};
    } else if (typeof props.selectedFilter === "string") {
        filters.value = {type: props.selectedFilter, os: "all", ram: "all", team: "all", owner: "all", status: "all"};
    }
};

// Watch for external filter changes
watch(() => props.selectedFilter, (newFilter) => {
    if (typeof newFilter === "object") {
        filters.value = {...newFilter};
    } else if (typeof newFilter === "string") {
        filters.value = {type: newFilter, os: "all", ram: "all", team: "all", owner: "all", status: "all"};
    }
}, {immediate: true});

// Initialize on mount
initializeFilters();
loadOwners();
</script>

<style scoped>
.filter-bar {
    margin-left: 50px;
    display: flex;
    align-items: center;
    gap: 20px;
    position: relative;
    z-index: 1;
    flex-wrap: wrap;
}

.filter-group {
    display: flex;
    align-items: center;
    gap: 8px;
}

.filter-group label {
    color: v-bind('COLORS.PRIMARY.WHITE');
    font-weight: 600;
    font-size: 0.9rem;
}

.filter-group select {
    padding: 8px 12px;
    background: rgba(255, 255, 255, 0.1);
    color: v-bind('COLORS.PRIMARY.WHITE');
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.9rem;
}

.filter-group select:focus {
    outline: none;
    border-color: v-bind('COLORS.PRIMARY.RED');
}

.clear-btn {
    padding: 8px 16px;
    background: v-bind('COLORS.PRIMARY.RED');
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.3s ease;
}

.clear-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}
</style>
